import type { WixAstroConfig } from '@wix/cli-plugin-core';
import { WIX_CONFIG_FILENAME } from '@wix/cli-core-definitions';
import {
  getExistingWixConfigPaths,
  importWixConfig,
  validateWixConfig,
} from '@wix/cli-plugin-core';
import { globby } from 'globby';
import type { WixConfigSchema } from './files/wix.config.js';

export async function importCliAstro(
  projectFolder: string,
  config?: WixConfigSchema
) {
  const existingConfigPaths = await getExistingWixConfigPaths(projectFolder);

  if (existingConfigPaths.dynamicConfigPath != null) {
    const definedWixConfig = await importWixConfig(projectFolder);
    return {
      astro: await importAstroCommands(definedWixConfig.config),
      definedWixConfig,
    };
  }

  if (
    config &&
    Object.hasOwn(config, 'appId') &&
    (hasSiteConfiguration(config) ||
      (await hasAstroConfigurationFile(projectFolder)))
  ) {
    return {
      astro: await importAstroCommands(config),
      definedWixConfig: {
        config: validateWixConfig(config, WIX_CONFIG_FILENAME),
      },
    };
  }
  return null;
}

async function importAstroCommands(config: WixAstroConfig | WixConfigSchema) {
  // Site projects only get the limited command set.
  if (hasSiteConfiguration(config)) {
    const { getLimitedRootCommand } = await import('@wix/cli-astro-commands');
    return { getRootCommand: getLimitedRootCommand };
  }
  return import('@wix/cli-astro-commands');
}

function hasSiteConfiguration(config: WixAstroConfig | WixConfigSchema) {
  const { site } = config as { site?: { outputDirectory?: unknown } };
  return site?.outputDirectory != null;
}

// https://docs.astro.build/en/basics/project-structure/#astroconfigmjs
async function hasAstroConfigurationFile(projectFolder: string) {
  const configFilesFound = await globby('astro.config.{js,cjs,mjs,ts}', {
    cwd: projectFolder,
    onlyFiles: true,
  });
  return configFilesFound.length === 1;
}
